/** Copyright (c) 2021-present, Trend Micro, Inc. All rights reserved. */
import { RootState } from "./root";

const STORE_C1_TOKEN = "STORE_C1_TOKEN";
const STORE_ACCOUNTS = "STORE_ACCOUNTS";
const ADD_LAST_SELECTED_ACCOUNT_ID = "ADD_LAST_SELECTED_ACCOUNT_ID";

export const storeCloudOneTokenAction = (c1Token: CloudOneToken): TokenAction => {
  return {
    type: STORE_C1_TOKEN,
    c1Token,
  };
};
export const storeCloudOneLastSelectedAccountAction = (c1AccountID: string): LastSelectedAccountAction => {
  return {
    type: ADD_LAST_SELECTED_ACCOUNT_ID,
    c1AccountID,
  };
};
export const storeCloudOneAccountsAction = (storeAccounts: CloudOneAccounts): AccountsAction => {
  return {
    type: STORE_ACCOUNTS,
    storeAccounts,
  };
};

type PartialDeep<T> = {
  [P in keyof T]?: PartialDeep<T[P]>;
};

export type CloudOneState = PartialDeep<typeof initialState>;

export type CloudOneToken = typeof initialState.c1Token;
type CloudOneAccounts = Array<{
  id: string;
  alias: string;
  locale: string;
  timezone: string;
  region: string;
  state: string;
  created: string;
  lastModified: string;
  urn: string;
  mfaRequired: boolean;
  links: Array<{
    rel: string;
    href: string;
    method: string;
  }>;
}>;

type TokenAction = {
  type: string;
  c1Token: CloudOneToken;
};
type AccountsAction = {
  type: string;
  storeAccounts: CloudOneAccounts;
};
type LastSelectedAccountAction = {
  type: string;
  c1AccountID: string;
};

const initialState = {
  c1Accounts: {
    requestAccounts: false,
    hasError: false,
    hasInitialAccountId: false,
    c1AccountID: "",
    c1AccountsList: [],
    lastAccessedAccountID: "",
    fetchRecInvitations: false,
    invitationsReceived: [],
    showInitialInvitationsPage: true,
    urlHasInvitationID: false,
  },
  c1Services: { c1ServicesList: [] },
  c1Token: { token: "", expires: "", serviceRoleUrnList: [] as string[] },
  c1UserDetails: { name: "", email: "", wsAdminID: "", type: "" },
  c1Common: {
    storeId: "c1c-container-security",
    locale: "en",
    theme: { colorMode: "light", overrides: {} },
  },
};

export default function reducer(state: CloudOneState = initialState, action) {
  switch (action.type) {
    case "ADD_ACCOUNT_ID":
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountID: action.c1AccountID,
        },
      };
    case "ADD_USER_TYPE":
      return {
        ...state,
        c1UserDetails: {
          type: action.userType,
        },
      };
    case ADD_LAST_SELECTED_ACCOUNT_ID:
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          hasInitialAccountId: true,
          lastAccessedAccountID: action.c1AccountID,
          c1AccountID: action.c1AccountID,
        },
      };
    case STORE_ACCOUNTS:
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountsList: action.storeAccounts,
        },
      };
    case "STORE_INIT":
      // rehydrate the states based on shell state (e.g. upon refresh)
      return {
        ...action.shellInitState,
        c1Common: {
          ...action.shellInitState.c1Common,
          storeId: "c1c-container-security",
        },
      };
    case "SET_LOCALE":
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          locale: action.locale,
        },
      };
    case "STORE_SERVICES":
      return {
        ...state,
        c1Services: {
          c1ServicesList: action.c1ServicesList,
        },
      };
    case STORE_C1_TOKEN:
      return {
        ...state,
        c1Token: {
          ...action.c1Token,
        },
      };
    case "SET_THEME":
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: action.theme,
        },
      };
    case "SET_COLOR_MODE":
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state?.c1Common?.theme,
            colorMode: action.colorMode,
          },
        },
      };
    case "SET_THEME_OVERRIDES":
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state?.c1Common?.theme,
            overrides: action.overrides,
          },
        },
      };
    case "RESET_STORE":
      return {
        ...state,
        ...initialState,
      };
    default:
      return state;
  }
}

export const isUserReadonly = (state: RootState): boolean => {
  const serviceRoleUrnList = state?.cloudOne?.c1Token?.serviceRoleUrnList ?? [];
  // serviceRoleUrnList contains roles per microfrontend. First find the urn specific to container-security.
  const containerServiceRole = serviceRoleUrnList?.find((serviceUrn) =>
    serviceUrn?.includes("urn:cloudone:container:"),
  );
  // Role type is found at end of string. If type cannot be determined, default to read-only
  // Example format: "urn:cloudone:container:ca-1:333196150634:role/full-access"
  const isFullAccess = containerServiceRole?.includes("full-access");
  return !isFullAccess;
};

export const getTokenInfo = (state: RootState, type: string) => state?.cloudOne.c1Token?.[type];
export const getCurrentAccountID = (state: RootState) => state?.cloudOne.c1Accounts?.c1AccountID;
export const getCommonType = (state: RootState, type: string) => state?.cloudOne.c1Common?.[type];
export const getServices = (state: RootState, type: string) => state?.cloudOne.c1Services?.[type];
export const getUserDetails = (state: RootState, type: string) => state?.cloudOne.c1UserDetails?.[type];
export const getAccountInfo = (state: RootState, accountID: string | undefined, key: string) => {
  const accountInfo = state?.cloudOne.c1Accounts?.c1AccountsList?.find((account: any) => account.id === accountID);
  return accountInfo?.[key];
};
export const getCurrentAccountInfo = (state: RootState, key: string) => {
  const accountID = getCurrentAccountID(state);
  return getAccountInfo(state, accountID, key);
};
